/**
 * Phone number helpers — the single source of truth for phone formatting.
 *
 * NANP-only (US + Canada): every number is a 10-digit North American number with an
 * optional leading `1` country code. 10 digits is a safe assumption everywhere, and the
 * 10-digit cap below is intentional, not a bug. International / non-NANP numbers are NOT
 * supported — if that ever changes, reach for libphonenumber-js.
 *
 * Canonical display/storage format: `XXX-XXX-XXXX` (dashes only — no brackets, no spaces).
 *
 * This module is imported by both the backend (Edge `formatPhone` global, server-side
 * normalization) and the frontend bundle (live input formatter), so keep it dependency-free.
 */

/** Accepted display format: 10-digit dashed, with an optional `1-` country prefix. */
export const PHONE_REGEX = /^(1-)?\d{3}-\d{3}-\d{4}$/;

/** Shared validation message, describing the expected format. Used by every phone validator. */
export const PHONE_FORMAT_MESSAGE = 'Enter a valid phone number in the format 123-456-7890';

/**
 * Reduce any input to its 10 significant NANP digits, dropping a leading country-code `1`.
 * Returns null when the value isn't a valid 10-digit NANP number.
 *
 * Dropping a leading `1` is safe per NANP: a valid 10-digit number's area code can never
 * start with 0 or 1, so an 11-digit string starting with `1` is unambiguously a country code.
 */
export function getPhoneDigits(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  let digits = raw.replace(/\D+/g, '');

  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.substring(1);
  }

  return digits.length === 10 ? digits : null;
}

/**
 * Group a string of NANP digits (partial or full, up to 10) into dashed form:
 *   ''      -> ''
 *   '416'   -> '416'
 *   '41655' -> '416-55'
 *   '4165551234' -> '416-555-1234'
 *
 * Used by both the live input formatter and {@link formatPhone} so the dash grouping is
 * defined in exactly one place.
 */
export function groupNanpDigits(digits: string): string {
  if (digits.length < 4) {
    return digits;
  }
  if (digits.length < 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Normalize any reasonable phone input to the canonical `XXX-XXX-XXXX` form.
 * Falls back to the original trimmed string when it can't be parsed as a 10-digit NANP
 * number, so display never silently blanks out unexpected data.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }

  const digits = getPhoneDigits(raw);
  return digits ? groupNanpDigits(digits) : raw.trim();
}

/** True when the value is (or normalizes to) a valid 10-digit NANP number. */
export function isValidPhone(raw: string | null | undefined): boolean {
  return getPhoneDigits(raw) !== null;
}
